import { z } from "zod";

interface PermissionDefinition {
  /** A short label for the permission, starting with its verb. */
  name: string;
  description: string;
  /** Only an admin can create, edit, archive, assign, change, or unassign a role that holds it. */
  adminOnly?: true;
}

/** Every staff permission a role can hold, keyed `<resource>.<verb>`. */
export const PERMISSIONS = {
  "accounts.ban": {
    name: "Ban accounts from the whole site",
    description:
      "Ban an account from Pax for what it posted, and lift those bans. Every full-site ban is permanent and can't be appealed.",
    adminOnly: true,
  },
  "agents.manage": {
    name: "Manage agents and evals",
    description: "Build, run, and review agents and evals, and see the traces of their games.",
  },
  "assets.moderate": {
    name: "Moderate assets",
    description:
      "Decide reports and appeals about assets and collections, and read a user's asset moderation record.",
  },
  "assets.lead_moderate": {
    name: "Lead-moderate assets",
    description:
      "Decide escalated asset and collection reports, and reclassify any judgement of one, which recounts the user's points and bans.",
  },
  "asset_signals.review": {
    name: "Review asset signals",
    description:
      "Set whether a published asset version was made with generative AI, and mark it human verified once checked that it is what it says it is.",
  },
  "asset_moderation_stats.view": {
    name: "View asset moderation stats",
    description:
      "See how asset and collection moderation is going: report and appeal outcomes, time to decision, automod's speed and accuracy, and each moderator's work.",
  },
  "comments.moderate": {
    name: "Moderate comments",
    description: "Decide reports about comments, and read a user's comment moderation record.",
  },
  "comments.lead_moderate": {
    name: "Lead-moderate comments",
    description:
      "Decide escalated comment reports, and reclassify any judgement of a comment, which recounts the user's points and bans.",
  },
  "comment_moderation_stats.view": {
    name: "View comment moderation stats",
    description:
      "See how comment moderation is going: report outcomes, time to decision, automod's speed and accuracy, and each moderator's work.",
  },
  "credits.view": {
    name: "View credits",
    description: "See any player's balances, history, and earnings, and the ledger reports.",
    adminOnly: true,
  },
  "credits.manage": {
    name: "Manage support credits",
    description: "Grant and remove support credits.",
    adminOnly: true,
  },
  "live_games.moderate": {
    name: "Moderate live games",
    description:
      "Decide appeals against live game bans, judging again everything the player wrote in the window that caused the ban.",
  },
  "live_game_moderation_stats.view": {
    name: "View live game moderation stats",
    description:
      "See how live game moderation is going: what automod flagged and redacted, by category, its speed and failures, and the bans it led to.",
  },
  "official_collections.manage": {
    name: "Curate official collections",
    description:
      "Create, edit, and delete official asset collections, private ones included, and add or remove their assets.",
  },
  "presets.moderate": {
    name: "Moderate presets",
    description: "Decide reports about presets, and read a user's preset moderation record.",
  },
  "presets.lead_moderate": {
    name: "Lead-moderate presets",
    description:
      "Decide escalated preset reports, and reclassify any judgement of a preset, which recounts the user's points and bans.",
  },
  "preset_moderation_stats.view": {
    name: "View preset moderation stats",
    description:
      "See how preset moderation is going: report outcomes, time to decision, and each moderator's work.",
  },
  "profiles.moderate": {
    name: "Moderate profiles",
    description:
      "Decide reports and appeals about profiles, and read a user's profile moderation record.",
  },
  "profiles.lead_moderate": {
    name: "Lead-moderate profiles",
    description:
      "Decide escalated profile reports, and reclassify any judgement of a profile, which recounts the user's points and bans.",
  },
  "profile_moderation_stats.view": {
    name: "View profile moderation stats",
    description:
      "See how profile moderation is going: report and appeal outcomes, time to decision, automod's speed and accuracy, and each moderator's work.",
  },
  "sandbox_purchases.make": {
    name: "Make sandbox purchases",
    description:
      "Start purchases where no real money moves: on a deployment whose Stripe key is in test mode, or in the App Store sandbox.",
  },
  "staff.manage": {
    name: "Manage staff",
    description: "Create staff roles, assign them to people, and keep staff member records.",
    adminOnly: true,
  },
} as const satisfies Record<`${string}.${string}`, PermissionDefinition>;

export const Permission = z
  .enum(Object.keys(PERMISSIONS) as [keyof typeof PERMISSIONS, ...(keyof typeof PERMISSIONS)[]])
  .meta({
    id: "Permission",
    description: "A staff permission's key, `<resource>.<verb>`.",
  });
export type Permission = z.infer<typeof Permission>;

export function isAdminOnly(permission: Permission): boolean {
  return "adminOnly" in PERMISSIONS[permission];
}

function isPermission(key: string): key is Permission {
  return Object.hasOwn(PERMISSIONS, key);
}

/** The keys that are known permissions, without duplicates, sorted. */
export function knownPermissions(keys: readonly string[]): Permission[] {
  return [...new Set(keys.filter(isPermission))].sort();
}

/** Every permission, sorted. */
export const ALL_PERMISSIONS = knownPermissions(Object.keys(PERMISSIONS));
